// 商品ページにギフトオプション追加_2026.4.15_佐藤

// custom-gift-cart-sync.js

// 新設計版:
// - メイン商品には _gift_group_id のみ
// - ギフト商品には _gift_group_id / _gift_type などを保持
// - ギフト商品削除時はその行だけ削除
// - メイン商品削除時のみ、同じ _gift_group_id のギフト商品を一緒に削除

(function () {
  'use strict';

  const DEBUG = true;
  const SNAPSHOT_KEY = 'giftSync_currentCartState';

  let isInternalCartUpdate = false;
  let isSyncRunning = false;

  function log(label, data) {
    if (!DEBUG) return;
    console.log(`[gift-sync] ${label}`, data || '');
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { cache: 'no-store' });
    if (!res.ok) throw new Error(`Failed to fetch cart: ${res.status}`);
    return await res.json();
  }

  function normalizeCartState(cart) {
    return (cart.items || []).map(item => ({
      key: item.key,
      id: item.id,
      variant_id: item.variant_id,
      quantity: item.quantity,
      title: item.title,
      product_type: item.product_type,
      properties: item.properties || {}
    }));
  }

  function getStorageJSON(key) {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error(`Failed to parse sessionStorage: ${key}`, e);
      return null;
    }
  }

  function setStorageJSON(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.error(`Failed to set sessionStorage: ${key}`, e);
    }
  }

  function isGiftItem(item) {
    return !!(item &&
      item.product_type === 'other' &&
      item.properties &&
      item.properties._gift_group_id &&
      item.properties._gift_type
    );
  }

  function isGiftLinkedMainItem(item) {
    return !!(item &&
      item.product_type !== 'other' &&
      item.properties &&
      item.properties._gift_group_id &&
      !item.properties._gift_type
    );
  }

  async function saveCurrentCartState() {
    const cart = await fetchCart();
    const normalized = normalizeCartState(cart);
    setStorageJSON(SNAPSHOT_KEY, normalized);
    log('Saved current cart state', normalized);
    return cart;
  }

  async function removeLineItemByKey(itemKey) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: itemKey,
        quantity: 0
      })
    });

    if (!res.ok) {
      throw new Error(`Failed to remove line item: ${itemKey}`);
    }

    return await res.json().catch(() => null);
  }

  async function removeOrphanGiftItems(previousState, currentCart) {
    const currentItems = currentCart.items || [];
    const currentMainGroupIds = new Set(
      currentItems
        .filter(isGiftLinkedMainItem)
        .map(item => item.properties._gift_group_id)
        .filter(Boolean)
    );

    const giftItemsToRemove = currentItems.filter(item => {
      if (!isGiftItem(item)) return false;
      const groupId = item.properties._gift_group_id;
      return groupId && !currentMainGroupIds.has(groupId);
    });

    if (!giftItemsToRemove.length) {
      log('No orphan gift items found');
      return false;
    }

    log('Removing orphan gift items', giftItemsToRemove);

    isInternalCartUpdate = true;
    try {
      for (const giftItem of giftItemsToRemove) {
        await removeLineItemByKey(giftItem.key);
      }
      return true;
    } finally {
      isInternalCartUpdate = false;
    }
  }

  async function syncGiftItems() {
    if (isInternalCartUpdate) {
      log('sync skipped because internal cart update is running');
      return;
    }

    if (isSyncRunning) {
      log('sync skipped because already running');
      return;
    }

    isSyncRunning = true;

    try {
      const previousState = getStorageJSON(SNAPSHOT_KEY) || [];
      const currentCart = await fetchCart();

      log('sync start - previousState', previousState);
      log('sync start - currentCart', normalizeCartState(currentCart));

      const changed = await removeOrphanGiftItems(previousState, currentCart);

      const refreshedCart = changed ? await fetchCart() : currentCart;
      setStorageJSON(SNAPSHOT_KEY, normalizeCartState(refreshedCart));

      log('sync finished', {
        changed,
        finalCart: normalizeCartState(refreshedCart)
      });
    } catch (e) {
      console.error('Failed to sync gift items:', e);
    } finally {
      isSyncRunning = false;
    }
  }

  function isCartMutationRequest(url, method) {
    if (!url) return false;
    const m = (method || 'GET').toUpperCase();

    return (
      m === 'POST' &&
      (
        url.includes('/cart/change.js') ||
        url.includes('/cart/update.js') ||
        url.includes('/cart/add.js') ||
        url.includes('/cart/clear.js')
      )
    );
  }

  const originalFetch = window.fetch;
  window.fetch = async function (...args) {
    const [input, options] = args;
    const requestUrl = typeof input === 'string' ? input : input?.url || '';
    const method = (options?.method || 'GET').toUpperCase();

    if (!isInternalCartUpdate && isCartMutationRequest(requestUrl, method)) {
      try {
        await saveCurrentCartState();
      } catch (e) {
        console.error('Failed to save cart state before mutation', e);
      }
    }

    const result = await originalFetch.apply(this, args);

    if (!isInternalCartUpdate && isCartMutationRequest(requestUrl, method)) {
      setTimeout(() => {
        syncGiftItems();
      }, 250);
    }

    return result;
  };

  (function patchXHR() {
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function(method, url) {
      this._giftSyncMethod = method;
      this._giftSyncUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function() {
      const url = this._giftSyncUrl || '';
      const method = (this._giftSyncMethod || 'GET').toUpperCase();

      if (!isInternalCartUpdate && isCartMutationRequest(url, method)) {
        saveCurrentCartState().catch(e => {
          console.error('Failed to save cart state before XHR mutation', e);
        });

        this.addEventListener('loadend', function() {
          setTimeout(() => {
            syncGiftItems();
          }, 250);
        });
      }

      return originalSend.apply(this, arguments);
    };
  })();

  async function init() {
    try {
      await saveCurrentCartState();
    } catch (e) {
      console.error('Failed to initialize gift sync', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();